import addFormatsModule from 'ajv-formats';
import { Ajv } from 'ajv';
import { Ajv2020 } from 'ajv/dist/2020.js';
// ajv-formats is CommonJS; NodeNext exposes its callable through the default namespace.
const addFormats = addFormatsModule;
const DIALECTS = {
    'http://json-schema.org/draft-07/schema': 'draft-07',
    'https://json-schema.org/draft/2020-12/schema': '2020-12',
};
/** Reads the dialect from $schema; schemas without $schema use 2020-12. Unsupported dialects throw. */
export function schemaDialect(schema) {
    const declared = typeof schema === 'object' ? schema.$schema : undefined;
    if (declared === undefined)
        return '2020-12';
    const dialect = typeof declared === 'string' ? DIALECTS[declared.replace(/#$/, '')] : undefined;
    if (!dialect)
        throw new Error(`Unsupported JSON Schema dialect: ${JSON.stringify(declared)}. Supported: ${Object.keys(DIALECTS).join(', ')}.`);
    return dialect;
}
export function createSchemaAjv(annotationKeywords = [], metaSchemas = [], annotationKeywordSchemas = {}, dialect = '2020-12', strict = true) {
    const options = {
        allErrors: true,
        strict,
        strictSchema: strict,
        validateSchema: true,
        useDefaults: false,
        passContext: true,
        discriminator: true,
        code: { source: true },
    };
    const ajv = dialect === 'draft-07' ? new Ajv(options) : new Ajv2020(options);
    addFormats.default(ajv);
    for (const keyword of annotationKeywords)
        ajv.addKeyword({ keyword, valid: true, metaSchema: {}, errors: false });
    for (const [keyword, metaSchema] of Object.entries(annotationKeywordSchemas)) {
        ajv.addKeyword({ keyword, valid: true, metaSchema: metaSchema, errors: false });
    }
    for (const schema of metaSchemas) {
        if (typeof schema !== 'object')
            throw new Error('Dialect meta-schemas must be objects with an $id.');
        ajv.addMetaSchema(schema);
    }
    return ajv;
}
export function schemaId(schema) {
    return typeof schema === 'object' && typeof schema.$id === 'string' ? schema.$id : undefined;
}
/** Wraps an Ajv validator; fragments resolve against the root schema's $id within the same Ajv instance. */
export function wrapSchemaValidator(ajv, validate, rootId) {
    return {
        validate(value, context) {
            const valid = context === undefined ? validate(value) : validate.call(context, value);
            if (typeof valid !== 'boolean')
                throw new Error('Asynchronous schemas are not supported.');
            const issues = (validate.errors ?? []).map(error => ({ ...error }));
            return { valid, issues: valid ? [] : issues };
        },
        fragment(pointer) {
            if (!rootId)
                throw new Error('Schema fragments require a root schema with an $id.');
            const fragment = ajv.getSchema(`${rootId}${pointer.startsWith('#') ? pointer : `#${pointer}`}`);
            return fragment ? wrapSchemaValidator(ajv, fragment, rootId) : undefined;
        },
    };
}
/** Compiles one schema in a fresh Ajv instance for its declared dialect, without retaining state between calls. */
export function compileSchema(schema, options = {}) {
    const ajv = createSchemaAjv(options.annotationKeywords, options.metaSchemas, options.annotationKeywordSchemas, schemaDialect(schema), options.strict ?? true);
    for (const reference of options.references ?? [])
        ajv.addSchema(reference);
    return wrapSchemaValidator(ajv, ajv.compile(schema), schemaId(schema));
}
/** Deterministic JSON with sorted object keys, used to compare schema content. */
export function canonicalSchemaJson(value) {
    if (Array.isArray(value))
        return `[${value.map(canonicalSchemaJson).join(',')}]`;
    if (value && typeof value === 'object') {
        return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${canonicalSchemaJson(value[key])}`).join(',')}}`;
    }
    return JSON.stringify(value) ?? 'null';
}
